import {starterPlanSearch} from "../../views/MagicMealPlan/magicMealPlanUrlHelpers";
import React, { useReducer, useRef } from "react";
import axios from "axios";
import Helpers from "../Helpers";
import Constants from "../Constants";
import { Link } from "react-router-dom";

const actions = {
  CHANGE_SECTION: "changeSection",
  LOAD_RESULTS: "loadResults",
  LOAD_FACETS: "loadFacets",
  LOADING: "loading",
  LOCK_FACETS: "lockFacets",
  REMOVE_RESULT: "removeResult",
  UPDATE_TERMS: "updateTerms",
  UPDATE_STASH_COUNTERS: "updateStashCounters",
  INCREMENT_REVIEWED_COUNTER: "incrementReviewedCounter",
}

const sections = {
  ALL: "all",
  PINNED: "pinned",
  SAVED: "saved",
  LOVED: "loved",
  MADE: "made",
  REVIEWED: "reviewed",
}

const searchActions = {
  SEARCH_BUTTON: "searchButton",
  SECTION_ALL: "sectionAll",
  SECTION_PINNED: "sectionPinned",
  SECTION_SAVED: "sectionSaved",
  SECTION_LOVED: "sectionLoved",
  SECTION_MADE: "sectionMade",
  SECTION_REVIEWED: "sectionReviewed",
  PAGER: "pager",
  FACET: "facet",
  RECOMMENDED_ONLY: "recommendedOnly",
  CLEAR_FILTERS: "clearFilters",
}

export default function useUniversalSearch(token, config, preferences) {
  const { buildQuery, isNotEmpty } = Helpers();
  const { documentTypes, searchOrigins } = Constants();
  const collectionSearchEnabled = config.collectionSearchEnabled === true;
  const pinnedMealPlansSearchPriorityEnabled = config.pinnedMealPlansSearchPriorityEnabled === true;

  function normalizeSection(section) {
    if (pinnedMealPlansSearchPriorityEnabled && section === sections.PINNED) {
      return sections.ALL;
    }
    return Object.values(sections).indexOf(section) === -1 ? sections.ALL : section;
  }

  function normalizeDocumentType(documentType) {
    if (!collectionSearchEnabled && documentType === documentTypes.TILE_COLLECTION) {
      return "";
    }
    return documentType;
  }

  function visibleFacets(facets) {
    if (collectionSearchEnabled) {
      return facets;
    }
    return {
      ...facets,
      documentTypes: (facets.documentTypes || []).filter(f => f.facetName !== documentTypes.TILE_COLLECTION),
    };
  }

  const initialState = {
    section: sections.ALL,
    recommendedOnly: config.recommendedOnly,
    terms: "",
    results: [],
    activeDocumentType: config.searchOrigin === searchOrigins.PLANNER ? documentTypes.RECIPE : "",
    activeToolIds: [],
    activeCharacteristicIds: [],
    activeTagIds: config.tagIds,
    activeRatingIds: [],
    activePlanType: "",
    total: 0,
    totalPinned: 0,
    totalSaved: 0,
    totalLoved: 0,
    totalMade: 0,
    totalReviewed: 0,
    offset: 0,
    facets: {
      documentTypes: [],
      planTypeFacets: [],
      toolFacets: [],
      characteristicFacets: [],
      tagFacets: [],
      ratingFacets:[],
      magicMealPlannerFacets: [],
    },
    facetLocked: true,
    loading: true,
  }
  function reducer(state, action) {
    if (action.type === actions.LOADING) {
      return { ...state, loading: true };
    } else if (action.type === actions.CHANGE_SECTION) {
      return { ...state, section: action.data.section };
    } else if (action.type === actions.LOAD_RESULTS) {
      if (config.searchOrigin === searchOrigins.PLANNER) {
        // Persist the "Meal Planner friendly" facet for the current Magic Meal Planner session
        config.setRecommendedOnly(action.data.recommendedOnly);
      }
      return {
        ...state,
        section: action.data.section,
        recommendedOnly: action.data.recommendedOnly,
        terms: state.terms === "" ? action.data.terms : state.terms,
        results: action.data.results,
        activeDocumentType: normalizeDocumentType(action.data.activeDocumentType),
        activeToolIds: action.data.activeToolIds,
        activeCharacteristicIds: action.data.activeCharacteristicIds,
        activeTagIds: action.data.activeTagIds,
        activeRatingIds: action.data.activeRatingIds,
        activePlanType: action.data.activePlanType,
        total: action.data.total,
        totalPinned: action.data.totalPinned,
        totalSaved: action.data.totalSaved,
        totalLoved: action.data.totalLoved,
        totalMade: action.data.totalMade,
        totalReviewed: action.data.totalReviewed,
        offset: action.data.offset,
        loading: false,
      };
    } else if (action.type === actions.LOAD_FACETS) {
      return { ...state, facets: visibleFacets(action.data.facets), facetLocked: false };
    } else if (action.type === actions.LOCK_FACETS) {
      return { ...state, facetLocked: true };
    } else if (action.type === actions.REMOVE_RESULT) {
      const documentId = action.data.documentId?.toString();
      return {
        ...state,
        results: state.results.filter(result => (
          result.documentId?.toString() !== documentId ||
          result.documentType !== action.data.documentType
        )),
      };
    } else if (action.type === actions.UPDATE_TERMS) {
      return { ...state, terms: action.data.terms };
    } else if (action.type === actions.UPDATE_STASH_COUNTERS) {
      function delta(beforeValue, afterValue) {
        if (beforeValue === afterValue) {
          return 0;
        }
        return afterValue ? 1 : -1;
      }
      const totalPinned = Math.max(0, state.totalPinned + delta(action.data.before.pinned, action.data.after.pinned));
      const totalSaved = Math.max(0, state.totalSaved + delta(action.data.before.saved, action.data.after.saved));
      const totalLoved = Math.max(0, state.totalLoved + delta(action.data.before.loved, action.data.after.loved));
      return { ...state, totalPinned: totalPinned, totalSaved: totalSaved, totalLoved: totalLoved };
    } else if (action.type === actions.INCREMENT_REVIEWED_COUNTER) {
      return { ...state, totalReviewed: state.totalReviewed + 1 };
    }
    return state;
  }
  const [state, dispatch] = useReducer(reducer, initialState);
  const latestSearchRequestId = useRef(0);

  function changeSection(e, section) {
    e.preventDefault();
    dispatch({
      type: actions.CHANGE_SECTION,
      data: { section: section }
    });
  }

  function updateTerms(terms) {
    dispatch({
      type: actions.UPDATE_TERMS,
      data: { terms: encodeURIComponent(terms) }
    });
  }

  function removeResult(documentId, documentType) {
    dispatch({
      type: actions.REMOVE_RESULT,
      data: { documentId: documentId, documentType: documentType }
    });
  }

  function nextLocation(searchAction, data) {
    dispatch({ type: actions.LOADING });
    let prefix;
    if (config.searchOrigin === searchOrigins.PLANNER) {
      prefix = `${config.path}${starterPlanSearch("?v=search", config.plannerPlan)}&`
    } else {
      prefix = `${config.path}?`
    }
    let url = prefix;
    const activeDocumentType = normalizeDocumentType(state.activeDocumentType);
    const activeSearchParameters = {
      section: state.section,
      recommendedOnly: state.recommendedOnly,
      terms: state.terms,
      offset: 0,
      documentType: activeDocumentType,
      planType: state.activePlanType,
      toolIds: state.activeToolIds.join(","),
      ratingIds: state.activeRatingIds.join(","),
      characteristicIds: state.activeCharacteristicIds.join(","),
      tagIds: state.activeTagIds.join(",")
    };
    function searchUrl(overrides) {
      const params = { ...activeSearchParameters, ...overrides };
      return `${prefix}tab=${encodeURIComponent(params.section)}&mmp-friendly=${encodeURIComponent(params.recommendedOnly)}&query=${params.terms}&offset=${encodeURIComponent(params.offset)}&content=${encodeURIComponent(params.documentType)}&wp-type=${encodeURIComponent(params.planType)}&tools=${encodeURIComponent(params.toolIds)}&ratings=${encodeURIComponent(params.ratingIds)}&allergens=${encodeURIComponent(params.characteristicIds)}&categories=${encodeURIComponent(params.tagIds)}`;
    }
    if (searchAction === searchActions.SECTION_ALL) {
      url = searchUrl({ section: sections.ALL });
    } else if (searchAction === searchActions.SECTION_PINNED) {
      url = searchUrl({ section: sections.PINNED });
    } else if (searchAction === searchActions.SECTION_SAVED) {
      url = searchUrl({ section: sections.SAVED });
    } else if (searchAction === searchActions.SECTION_LOVED) {
      url = searchUrl({ section: sections.LOVED });
    } else if (searchAction === searchActions.SECTION_MADE) {
      url = searchUrl({ section: sections.MADE });
    } else if (searchAction === searchActions.SECTION_REVIEWED) {
      url = searchUrl({ section: sections.REVIEWED });
    } else if (searchAction === searchActions.RECOMMENDED_ONLY) {
      url = searchUrl({ recommendedOnly: !state.recommendedOnly });
    } else if (searchAction === searchActions.SEARCH_BUTTON) {
      url = searchUrl({});
    } else if (searchAction === searchActions.PAGER) {
      url = searchUrl({ offset: data });
    } else if (searchAction === searchActions.FACET) {
      dispatch({ type: actions.LOCK_FACETS });
      if (data.facetType === "documentType") {
        const dt = normalizeDocumentType(activeDocumentType === data.facetName ? "" : data.facetName);
        url = searchUrl({ documentType: dt });
      } else if (data.facetType === "tool") {
        const toolIds = state.activeToolIds.indexOf(data.id) === -1 ? state.activeToolIds.concat([data.id]) : state.activeToolIds.filter(id => id !== data.id);
        url = searchUrl({ toolIds: toolIds.join(",") });
      } else if (data.facetType === "rating") {
        const ratingIds = state.activeRatingIds.indexOf(data.id) === -1 ? state.activeRatingIds.concat([data.id]) : state.activeRatingIds.filter(id => id !== data.id);
        url = searchUrl({ ratingIds: ratingIds.join(",") });
      } else if (data.facetType === "characteristic") {
        const characteristicIds = state.activeCharacteristicIds.indexOf(data.id) === -1 ? state.activeCharacteristicIds.concat([data.id]) : state.activeCharacteristicIds.filter(id => id !== data.id);
        url = searchUrl({ characteristicIds: characteristicIds.join(",") });
      } else if (data.facetType === "planType") {
        const pt = state.activePlanType === data.facetName ? "" : data.facetName;
        url = searchUrl({ planType: pt });
      } else if (data.facetType === "tag") {
        const tagIds = state.activeTagIds.indexOf(data.id) === -1 ? state.activeTagIds.concat([data.id]) : state.activeTagIds.filter(id => id !== data.id);
        url = searchUrl({ tagIds: tagIds.join(",") });
      }
    } else if (searchAction === searchActions.CLEAR_FILTERS) {
      url = searchUrl({ recommendedOnly: false, documentType: "", planType: "", toolIds: "", ratingIds: "", characteristicIds: "", tagIds: "" });
    }
    return config.searchOrigin === searchOrigins.PLANNER ? `${url}${config.location?.hash || ""}` : url;
  }

  function validSearch(search) {
    const searchParams = new URLSearchParams(search);
    if (
      searchParams.get("tab") === null ||
      searchParams.get("mmp-friendly") === null ||
      searchParams.get("query") === null ||
      searchParams.get("offset") === null ||
      searchParams.get("content") === null ||
      searchParams.get("wp-type") === null ||
      searchParams.get("tools") === null ||
      searchParams.get("ratings") === null ||
      searchParams.get("allergens") === null ||
      searchParams.get("categories") === null
    ) {
      // At least one parameter is missing, we generate a valid string
      const section = normalizeSection(searchParams.get("tab") === null ? sections.ALL : searchParams.get("tab"));
      // Handle special Magic Meal Planner use case
      let recommendedOnly;
      if (searchParams.get("mmp-friendly") === null) {
        if (config.searchOrigin === searchOrigins.PLANNER) {
          // We use the meal planner persisted value stored in the config
          recommendedOnly = config.recommendedOnly;
        } else {
          recommendedOnly = false;
        }
      } else {
        recommendedOnly = searchParams.get("mmp-friendly");
      }
      const terms = searchParams.get("query") === null ? "" : searchParams.get("query");
      const offset = searchParams.get("offset") === null ? 0 : searchParams.get("offset");
      // Force Recipe document type when searching in the Magic Meal Planner
      let dt = ""
      if (config.searchOrigin === searchOrigins.PLANNER) {
        dt = documentTypes.RECIPE;
      } else if (searchParams.get("content") !== null) {
        dt = searchParams.get("content");
      }
      dt = normalizeDocumentType(dt);
      const pt = searchParams.get("wp-type") === null ? "" : searchParams.get("wp-type");
      const toolIds = searchParams.get("tools") === null ? "" : searchParams.get("tools");
      const ratingIds = searchParams.get("ratings") === null ? "" : searchParams.get("ratings");
      // Apply allergens when searching for recipes without any characteristics in the URL
      let characteristicIds = "";
      if (dt === documentTypes.RECIPE && searchParams.get("allergens") === null && preferences.allergens.length > 0) {
        characteristicIds = preferences.allergens.map(a => a.id).join(",");
      } else if (searchParams.get("allergens") !== null) {
        characteristicIds = searchParams.get("allergens");
      }
      let tagIds = searchParams.get("categories") === null ? config.tagIds.join(",") : searchParams.get("categories");
      const s = `tab=${encodeURIComponent(section)}&mmp-friendly=${encodeURIComponent(recommendedOnly.toString())}&query=${encodeURIComponent(terms)}&offset=${encodeURIComponent(offset)}&content=${encodeURIComponent(dt)}&wp-type=${encodeURIComponent(pt)}&tools=${encodeURIComponent(toolIds)}&ratings=${encodeURIComponent(ratingIds)}&allergens=${encodeURIComponent(characteristicIds)}&categories=${encodeURIComponent(tagIds)}`;
      if (config.searchOrigin === searchOrigins.PLANNER) {
        return starterPlanSearch(`?v=search&${s}`, config.plannerPlan);
      } else {
        return `?${s}`;
      }
    } else if (normalizeSection(searchParams.get("tab")) !== searchParams.get("tab")) {
      const s = `tab=${normalizeSection(searchParams.get("tab"))}&mmp-friendly=${encodeURIComponent(searchParams.get("mmp-friendly"))}&query=${encodeURIComponent(searchParams.get("query"))}&offset=${encodeURIComponent(searchParams.get("offset"))}&content=${encodeURIComponent(searchParams.get("content"))}&wp-type=${encodeURIComponent(searchParams.get("wp-type"))}&tools=${encodeURIComponent(searchParams.get("tools"))}&ratings=${encodeURIComponent(searchParams.get("ratings"))}&allergens=${encodeURIComponent(searchParams.get("allergens"))}&categories=${encodeURIComponent(searchParams.get("categories"))}`;
      if (config.searchOrigin === searchOrigins.PLANNER) {
        return starterPlanSearch(`?v=search&${s}`, config.plannerPlan);
      } else {
        return `?${s}`;
      }
    } else if (!collectionSearchEnabled && searchParams.get("content") === documentTypes.TILE_COLLECTION) {
      const s = `tab=${encodeURIComponent(searchParams.get("tab"))}&mmp-friendly=${encodeURIComponent(searchParams.get("mmp-friendly"))}&query=${encodeURIComponent(searchParams.get("query"))}&offset=${encodeURIComponent(searchParams.get("offset"))}&content=&wp-type=${encodeURIComponent(searchParams.get("wp-type"))}&tools=${encodeURIComponent(searchParams.get("tools"))}&ratings=${encodeURIComponent(searchParams.get("ratings"))}&allergens=${encodeURIComponent(searchParams.get("allergens"))}&categories=${encodeURIComponent(searchParams.get("categories"))}`;
      if (config.searchOrigin === searchOrigins.PLANNER) {
        return starterPlanSearch(`?v=search&${s}`, config.plannerPlan);
      } else {
        return `?${s}`;
      }
    } else {
      if (config.searchOrigin === searchOrigins.PLANNER) {
        searchParams.set("v", "search");
        return starterPlanSearch(`?${searchParams.toString()}`, config.plannerPlan);
      }
      return search;
    }
  }

  function search(search) {
    const requestId = latestSearchRequestId.current + 1;
    latestSearchRequestId.current = requestId;
    const query = buildQuery(search);
    axios({
      method: "post",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      url: `${process.env.REACT_APP_API}/search`,
      data: query,
    }).then(res => {
      if (res.status === 200 && requestId === latestSearchRequestId.current) {
        dispatch({
          type: actions.LOAD_RESULTS,
          data: res.data
        });
      }
    }).catch(() => {
      // do nothing
    });
    axios({
      method: "post",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      url: `${process.env.REACT_APP_API}/search/facets`,
      data: query,
    }).then(res => {
      if (res.status === 200 && requestId === latestSearchRequestId.current) {
        dispatch({
          type: actions.LOAD_FACETS,
          data: { facets: res.data }
        });
      }
    }).catch(() => {
      // do nothing
    });
  }

  function sectionTotal() {
    let result = 0;
    if (state.section === sections.ALL) {
      result = state.total;
    } else if (state.section === sections.PINNED) {
      result = state.totalPinned;
    } else if (state.section === sections.SAVED) {
      result = state.totalSaved;
    } else if (state.section === sections.LOVED) {
      result = state.totalLoved;
    } else if (state.section === sections.MADE) {
      result = state.totalMade;
    } else if (state.section === sections.REVIEWED) {
      result = state.totalReviewed;
    }
    return result;
  }

  function noResultsMessage() {
    let result = null;
    if (state.section === sections.ALL && state.total === 0 && searchIsNotEmpty()) {
      result = "There is no content matching your current search, try to search for something else.";
    } else if (state.section === sections.PINNED && state.totalPinned === 0) {
      result = searchIsNotEmpty() ?
        "There are no pinned meal plans matching your current search, try to search for something else." : (
          <>You don't have any pinned meal plans yet! Search our <Link to="/search?content=WizardPlan">meal plans</Link> and pin your favorites.</>
        );
    } else if (state.section === sections.SAVED && state.totalSaved === 0) {
      if (config.searchOrigin === searchOrigins.PLANNER) {
        result = searchIsNotEmpty() ?
          "There are no saved recipes matching your current search, try to search for something else." : (
            <>
              You don't have any saved recipes yet! Dive into our <Link to={`${config.path}${starterPlanSearch("?v=search&content=Recipe", config.plannerPlan)}${config.location?.hash || ""}`}>recipes</Link> and save to your heart's desire!
            </>
          );
      } else {
        result = searchIsNotEmpty() ?
          "There are no saved plans or recipes matching your current search, try to search for something else." : (
            <>
              You don't have anything saved yet! Dive into our <Link to="/search?content=Recipe">recipes</Link> and <Link
                to="/search?content=WizardPlan">meal plans</Link> and save to your heart's desire!
            </>
          );
      }
    } else if (state.section === sections.LOVED && state.totalLoved === 0) {
      if (config.searchOrigin === searchOrigins.PLANNER) {
        result = searchIsNotEmpty() ?
          "There are no loved recipes matching your current search, try to search for something else." : (
            <>
              You don't have any loved recipes yet! Dive into our <Link to={`${config.path}${starterPlanSearch("?v=search&content=Recipe", config.plannerPlan)}${config.location?.hash || ""}`}>recipes</Link> and save to your heart's desire!
            </>
          );
      } else {
        result = searchIsNotEmpty() ?
          "There are no loved plans or recipes matching your current search, try to search for something else." : (
            <>
              Find something you love, tap on that heart and it will always be here for you. Start by searching our <Link to="/search?content=Recipe">recipes</Link> and <Link
                to="/search?content=WizardPlan">meal plans</Link>.
            </>
          );
      }
    } else if (state.section === sections.MADE && state.totalMade === 0) {
      if (config.searchOrigin === searchOrigins.PLANNER) {
        result = searchIsNotEmpty() ?
          "There are no made recipes matching your current search, try to search for something else." : (
            <>
              You don't have any made recipes yet. Start by searching our <Link to={`${config.path}${starterPlanSearch("?v=search&content=Recipe", config.plannerPlan)}${config.location?.hash || ""}`}>recipes</Link>.
            </>
          );
      } else {
        result = searchIsNotEmpty() ?
          "There are no made plans or recipes matching your current search, try to search for something else." : (
            <>
              You don't have anything made yet. Browse our <Link to="/search?content=Recipe">recipes</Link> and <Link to="/search?content=WizardPlan">meal plans</Link> and mark items as made.
            </>
          );
      }
    } else if (state.section === sections.REVIEWED && state.totalReviewed === 0) {
      if (config.searchOrigin === searchOrigins.PLANNER) {
        result = searchIsNotEmpty() ?
          "There are no reviewed recipes matching your current search, try to search for something else." : (
            <>
              You don't have any reviewed recipes yet. Start by searching our <Link to={`${config.path}${starterPlanSearch("?v=search&content=Recipe", config.plannerPlan)}${config.location?.hash || ""}`}>recipes</Link>.
            </>
          );
      } else {
        result = searchIsNotEmpty() ?
          "There are no reviewed plans or recipes matching your current search, try to search for something else." : (
            <>
              You don't have anything reviewed yet. Search our <Link to="/search?content=Recipe">recipes</Link> and <Link to="/search?content=WizardPlan">meal plans</Link> and leave a review.
            </>
          );
      }
    }
    return result;
  }

  function searchIsNotEmpty() {
    return isNotEmpty(state.terms) || filtersAreNotEmpty();
  }

  function filtersAreNotEmpty() {
    return (
      state.recommendedOnly ||
      isNotEmpty(state.activePlanType) ||
      (config.searchOrigin !== searchOrigins.PLANNER && isNotEmpty(state.activeDocumentType)) ||
      state.activeToolIds.length > 0 ||
      state.activeRatingIds.length > 0 ||
      state.activeCharacteristicIds.length > 0 ||
      state.activeTagIds.length > 0
    );
  }

  function updateStashCounters(data) {
    if (data && data.updated && data.before && data.after) {
      dispatch({
        type: actions.UPDATE_STASH_COUNTERS,
        data: data,
      });
    }
  }

  function incrementReviewedCounter() {
    dispatch({
      type: actions.INCREMENT_REVIEWED_COUNTER,
    });
  }

  return {
    sections: sections,
    searchActions: searchActions,
    state: state,
    changeSection: changeSection,
    filtersAreNotEmpty: filtersAreNotEmpty,
    nextLocation: nextLocation,
    noResultsMessage: noResultsMessage,
    incrementReviewedCounter: incrementReviewedCounter,
    removeResult: removeResult,
    updateTerms: updateTerms,
    validSearch: validSearch,
    search: search,
    searchIsNotEmpty: searchIsNotEmpty,
    sectionTotal: sectionTotal,
    updateStashCounters: updateStashCounters,
  }
}
